/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle
} from 'react';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import styles from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useFormSave} from '@ion/dls/src/hooks/useFormSave';
import {FormState} from '@ion/dls/src/hooks/useFormState';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import BulkSCIUpdateDetails, {BulkSCIUpdateProps} from './BulkSCIUpdateDetails';
import useSCIUpdate from './useSCIUpdate';

const useStyles = (theme: DLSTheme) => ({
  form : {
    margin                         : 'auto',
    minWidth                       : 'auto',
    minHeight                      : 'auto',
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    }
  },
  sciInfoText : {
    paddingTop : '25px',
    fontSize   : '16px'
  },
  infoIcon : {
    margin        : '0 6px',
    display       : 'inline-block',
    verticalAlign : 'middle',
    cursor        : 'pointer'
  },
  nonSCIText : {
    fontSize : '16px',
    padding  : '25px 0 15px 0'
  },
  sciSubTitleText : {
    fontSize : '12px'
  },
  sciInfoContent : {
    padding   : '0 0 0 17px',
    height    : '90%',
    overflowY : 'auto'
  },
  dropdown : {
    paddingTop  : '25px',
    paddingLeft : '15px'
  },
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '12px',
    fontFamily : 'Roboto',
    fontWeight : 400,
    marginLeft : '15px'
  }
});

interface SCIUpdateFormProps extends BulkSCIUpdateProps {
  setTaskNickNameAction: (action: string) => void;
  onSave: (details: SCIUpdateDetails) => void;
}

type SCIUpdateDetails = {
  deviceCommands: [{
    commandType: string,
    parameters: {}
  }];
}

export enum SCIUpdateDetailsFields {
  COMMAND_TYPE = 'SciDeviceSettings',
  UPDATED_SCI_SETTINGS = 'updatedSCISettings'
}

/**
 * Re-usable form with the details for SCI Update entity.
*/
export const SCIUpdateForm = forwardRef(({
  setTaskNickNameAction,
  onSave,
  setSelectedSCIDevices,
  selectedSCIDevices,
  setSelectedSCITypes,
  selectedSCITypes,
  setSelectedDeviceType,
  selectedDeviceType,
  selectedRowEntities,
  entitySelectionFormState,
  bulkImportParams,
  entity
}: SCIUpdateFormProps, ref) => {
  const editorStyles = useExtendStyles(styles, useStyles);
  const classes      = editorStyles();
  const path         = 'common:editors.bulkActions.sciUpdate';
  const {
    setFormState,
    formState,
    setShowSnackBar,
    showSnackBar,
    setErrorMessage,
    errorMessage,
    noSCIDevice,
    setUpdatedSCISettings,
    updatedSCISettings,
    setBulkSCISetting,
    bulkSCISetting,
    activeStep,
    typeOptions,
    onTypeChange,
    deviceTypes
  } = useSCIUpdate({
    setTaskNickNameAction,
    setSelectedSCIDevices,
    setSelectedDeviceType,
    setSelectedSCITypes,
    entitySelectionFormState,
    selectedRowEntities,
    bulkImportParams,
    entity
  });

  const handleSubmit = (details: SCIUpdateDetails) => {
    if (isEmpty(updatedSCISettings)) {
      setShowSnackBar(true);
    } else {
      onSave({
        ...details,
        deviceCommands : [{
          commandType : SCIUpdateDetailsFields.COMMAND_TYPE,
          parameters  : {
            ...updatedSCISettings
          }
        }]
      });
    }
    if (isEmpty(deviceTypes?.deviceType)) {
      setErrorMessage(true);
    }
  };

  const onSubmit = useFormSave({
    onSave : handleSubmit,
    formState,
    serialize,
    setFormState
  });

  const handleSnackBar = useCallback(() => {
    setShowSnackBar(false);
  }, []);

  const getErrorMessage = useCallback(() => {
    if (isEmpty(deviceTypes?.deviceType)) {
      return translate(`${path}.typeErrorMessage`);
    } else if (!selectedRowEntities?.length && !noSCIDevice) {
      return translate(`${path}.noSCIDeviceMessage`);
    } else if (isEmpty(updatedSCISettings)) {
      return translate(`${path}.noSettingsChangedMessage`);
    }
  }, [deviceTypes, updatedSCISettings, selectedRowEntities]);

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  return (
    <>
      <div className={classes.form}>
        <FormItem>
          <BulkSCIUpdateDetails selectedRowEntities={selectedRowEntities}
            selectedSCIDevices={selectedSCIDevices}
            selectedSCITypes={selectedSCITypes}
            selectedDeviceType={selectedDeviceType}
            setUpdatedSCISettings={setUpdatedSCISettings}
            updatedSCISettings={updatedSCISettings}
            setBulkSCISetting={setBulkSCISetting}
            bulkSCISetting={bulkSCISetting}
            setFormState={setFormState}
            activeStep={activeStep}
            typeOptions={typeOptions}
            onTypeChange={onTypeChange}
            errorMessage={errorMessage}
            deviceTypes={deviceTypes}
            path={path}
            classes={classes} />
        </FormItem>
      </div>
      {!isEmpty(deviceTypes?.deviceType) && (
        <IonSnackbar show={showSnackBar}
          message={getErrorMessage()}
          actionLabel={translate('common:editors.delete.ok')}
          onAction={handleSnackBar}
          onClose={handleSnackBar}
          autoHideDuration={3000} />
      )}
    </>
  );
});

const serialize = (formState: FormState): SCIUpdateDetails => {
  const {
    COMMAND_TYPE,
    UPDATED_SCI_SETTINGS
  } = SCIUpdateDetailsFields;
  return {
    deviceCommands : [{
      commandType : COMMAND_TYPE,
      parameters  : formState[UPDATED_SCI_SETTINGS]?.value
    }]
  };
};

export default memo(SCIUpdateForm);
